import { useContext } from "react";

import { AuthContext } from "~/auth/AuthProvider";
import { FeatureFlagName } from "~/gql/graphqlBase.generated";
import { GetAllFeatureFlagsQuery, useGetAllFeatureFlagsQuery } from "~/gql/queries/featureFlag.generated";

export interface UseFeatureFlagOptions {
	refetchOnFocus?: boolean;
	refetchOnMountOrArgChange?: boolean;
}

export interface UseFeatureFlagReturn {
	config: string;
	isAnswered: boolean;
	isEnabled: boolean;
	isError: boolean;
	isLoading: boolean;
}

export type FlagKey = Exclude<keyof GetAllFeatureFlagsQuery, "__typename">;

const flagsUnavailableToPortalCustomer: UseFeatureFlagReturn = {
	config: "",
	isAnswered: true,
	isEnabled: false,
	isError: false,
	isLoading: false
};

const useFeatureFlag = (name: FeatureFlagName, options?: UseFeatureFlagOptions): UseFeatureFlagReturn => {
	const { isCustomer } = useContext(AuthContext);
	// `isLoading` from RTK Query is false on the first render before the query
	// dispatches in useEffect — fold `isUninitialized` in so callers can treat
	// `isLoading` as "the flag is not yet resolved."
	const { flag, isAnswered, isError, isLoading, isUninitialized } = useGetAllFeatureFlagsQuery(undefined, {
		refetchOnFocus: options?.refetchOnFocus,
		refetchOnMountOrArgChange: options?.refetchOnMountOrArgChange,
		selectFromResult: ({ data, isError, isLoading, isUninitialized }) => ({
			flag: data?.[name as FlagKey] ?? undefined,
			isAnswered: data !== undefined,
			isError,
			isLoading,
			isUninitialized
		}),
		skip: isCustomer
	});

	if (isCustomer) return flagsUnavailableToPortalCustomer;

	return {
		config: flag?.config ?? "",
		isAnswered,
		isEnabled: flag?.enabled ?? false,
		isError,
		isLoading: isLoading || isUninitialized
	};
};

export default useFeatureFlag;
